import React, {useState, useRef, useEffect, useCallback} from 'react'
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  Box,
} from '@mui/material'
import {styled} from '@mui/material/styles'

// ─── Styled Components ──────────────────────────────────────────────────────
const Wrapper = styled(Box)({
  border: '1px solid #e5e7eb',
  borderRadius: 0,
  overflow: 'hidden',
  backgroundColor: '#fff',
})

const ScrollContainer = styled(TableContainer)({
  overflowX: 'auto',
  overflowY: 'auto',
  maxHeight: '72vh',
  '&::-webkit-scrollbar': {height: 6, width: 6},
  '&::-webkit-scrollbar-track': {background: '#f5f5f5'},
  '&::-webkit-scrollbar-thumb': {background: '#cbd5e1', borderRadius: 3},
})

const HeaderCell = styled(TableCell)({
  backgroundColor: '#f8fafc',
  color: '#374151',
  fontWeight: 700,
  fontSize: '0.7rem',
  textTransform: 'uppercase',
  letterSpacing: '0.03em',
  whiteSpace: 'nowrap',
  borderBottom: '2px solid #e2e8f0',
  padding: '10px 12px',
})

const GroupHeaderCell = styled(TableCell)({
  backgroundColor: '#eef2f7',
  color: '#1e3a5f',
  fontWeight: 700,
  fontSize: '0.65rem',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  textAlign: 'center',
  borderBottom: '1px solid #d1d5db',
  borderRight: '1px solid #e2e8f0',
  padding: '6px 12px',
})

const BodyRow = styled(TableRow, {
  shouldForwardProp: (p) => p !== 'isHighlight' && p !== 'isEven',
})(({isHighlight, isEven}) => ({
  ...(isHighlight && {
    backgroundColor: '#eff6ff !important',
    '& td': {
      fontWeight: 600,
      borderBottom: '2px solid #93c5fd',
    },
  }),
  ...(!isHighlight && isEven && {backgroundColor: '#fafbfc'}),
  '&:hover td': {
    backgroundColor: isHighlight ? '#dbeafe' : '#f0f7ff',
  },
}))

const BodyCell = styled(TableCell)({
  padding: '8px 12px',
  fontSize: '0.8rem',
  borderBottom: '1px solid #f1f5f9',
  color: '#374151',
  verticalAlign: 'middle',
})

const EmptyBox = styled(Box)({
  padding: '48px 16px',
  textAlign: 'center',
})

const PaginationBox = styled(Box)({
  borderTop: '1px solid #e5e7eb',
})

export default function MaterialTable({
  rows = [],
  columns = [],
  columnGroups = null,
  size = 'small',
  itemsPerPage = 10,
  noDataMessage = 'No data available',
  frozenColumnsCount = 0,
  minWidth = null,
  exportExcel = false,
  reportName = '',
}) {
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(itemsPerPage)
  const colHeaderRowRef = useRef(null)
  const [frozenOffsets, setFrozenOffsets] = useState([])

  // Calculate frozen column offsets from actual column header row (not group header)
  const measureFrozenOffsets = useCallback(() => {
    if (frozenColumnsCount <= 0 || !colHeaderRowRef.current) return
    const cells = colHeaderRowRef.current.querySelectorAll('th')
    if (!cells.length) return
    const offsets = []
    let left = 0
    for (let i = 0; i < frozenColumnsCount && i < cells.length; i++) {
      offsets.push(left)
      left += cells[i].getBoundingClientRect().width
    }
    setFrozenOffsets(offsets)
  }, [frozenColumnsCount])

  useEffect(() => {
    // Delay to ensure layout is done
    const timer = setTimeout(measureFrozenOffsets, 50)
    window.addEventListener('resize', measureFrozenOffsets)
    return () => {
      clearTimeout(timer)
      window.removeEventListener('resize', measureFrozenOffsets)
    }
  }, [measureFrozenOffsets, columns, rows])

  const pagedRows = rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)

  if (!rows.length) {
    return (
      <Wrapper>
        <EmptyBox>
          <Typography variant='body2' color='text.secondary'>{noDataMessage}</Typography>
        </EmptyBox>
      </Wrapper>
    )
  }

  // Fixed widths for frozen columns: col0 = 50px (#), col1 = 220px (Courier Partner)
  const FROZEN_WIDTHS = [50, 220]

  const getFrozenStyle = (colIdx, isHeader = false, rowBg) => {
    if (colIdx >= frozenColumnsCount) return {}
    const isLast = colIdx === frozenColumnsCount - 1
    let left = 0
    for (let i = 0; i < colIdx; i++) left += FROZEN_WIDTHS[i] || 120
    return {
      position: 'sticky',
      left,
      zIndex: isHeader ? 4 : 1,
      backgroundColor: isHeader ? '#f8fafc' : rowBg || '#fff',
      width: FROZEN_WIDTHS[colIdx] || 120,
      minWidth: FROZEN_WIDTHS[colIdx] || 120,
      maxWidth: FROZEN_WIDTHS[colIdx] || 120,
      ...(isLast && {
        borderRight: '1px solid #e2e8f0',
      }),
    }
  }

  return (
    <Wrapper>
      <ScrollContainer>
        <Table size={size} sx={{minWidth: minWidth || 'auto'}}>
          <TableHead>
            {columnGroups && columnGroups.length > 0 && (
              <TableRow>
                {(() => {
                  let colStart = 0
                  return columnGroups.map((group, idx) => {
                    const span = group.colSpan || group.span || 1
                    const isFrozenGroup = colStart < frozenColumnsCount
                    const frozenWidth = FROZEN_WIDTHS.reduce((s, w) => s + w, 0)
                    const cell = (
                      <GroupHeaderCell
                        key={idx}
                        colSpan={span}
                        sx={isFrozenGroup ? {
                          position: 'sticky',
                          left: 0,
                          zIndex: 4,
                          backgroundColor: '#eef2f7',
                          borderRight: '1px solid #e2e8f0',
                          width: frozenWidth,
                          minWidth: frozenWidth,
                        } : {}}
                      >
                        {group.label}
                      </GroupHeaderCell>
                    )
                    colStart += span
                    return cell
                  })
                })()}
              </TableRow>
            )}
            <TableRow>
              {columns.map((col, idx) => (
                <HeaderCell
                  key={idx}
                  align={col.align || 'left'}
                  sx={{
                    ...(col.headerStyle || {}),
                    ...getFrozenStyle(idx, true),
                  }}
                >
                  {col.label}
                </HeaderCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {pagedRows.map((row, rowIdx) => {
              const isHighlight = row._isWeightedAverage
              const isEven = rowIdx % 2 === 0
              const rowBg = isHighlight ? '#eff6ff' : isEven ? '#fafbfc' : '#fff'
              return (
                <BodyRow key={rowIdx} isHighlight={isHighlight} isEven={isEven}>
                  {columns.map((col, colIdx) => {
                    const cellStyle = typeof col.style === 'function' ? col.style(row) : col.style || {}
                    return (
                      <BodyCell
                        key={colIdx}
                        align={col.align || 'left'}
                        sx={{
                          ...cellStyle,
                          ...getFrozenStyle(colIdx, false, rowBg),
                        }}
                      >
                        {typeof col.value === 'function' ? col.value(row) : row[col.field]}
                      </BodyCell>
                    )
                  })}
                </BodyRow>
              )
            })}
          </TableBody>
        </Table>
      </ScrollContainer>
      {rows.length > rowsPerPage && (
        <PaginationBox>
          <TablePagination
            component='div'
            count={rows.length}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10))
              setPage(0)
            }}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        </PaginationBox>
      )}
    </Wrapper>
  )
}
